import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Send, Smile, Check } from 'lucide-react';
import { collection, query, orderBy, limit, getDocs, addDoc, serverTimestamp, updateDoc, doc, getDoc, where, writeBatch, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FIRESTORE_PATHS } from '../../config/appConfig';
import EmojiPicker from 'emoji-picker-react';
import logger from '../../utils/logger';

const ChatView = ({ friend, onBack, userId, friendOnline: passedFriendOnline }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [friendOnline, setFriendOnline] = useState(false);
    const messagesEndRef = useRef(null);

    // Sync with passed prop if available
    useEffect(() => {
        if (typeof passedFriendOnline !== 'undefined') {
            setFriendOnline(passedFriendOnline);
        }
    }, [passedFriendOnline]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Load messages using real-time onSnapshot (was 3s polling)
    useEffect(() => {
        if (!friend?.uid || !userId) return;

        const threadId = [userId, friend.uid].sort().join('_');

        const q = query(
            collection(db, FIRESTORE_PATHS.CHATS, threadId, FIRESTORE_PATHS.MESSAGES),
            orderBy('createdAt', 'asc'),
            limit(50)
        );

        const unsubscribe = onSnapshot(q, async (snap) => {
            const loadedMessages = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setMessages(loadedMessages);
            setLoading(false);

            // Mark incoming messages as SEEN in the snapshot callback
            const unreadIncoming = loadedMessages.filter(
                m => m.senderId === friend.uid && m.status !== 'seen'
            );

            if (unreadIncoming.length > 0) {
                try {
                    const batch = writeBatch(db);
                    unreadIncoming.forEach(msg => {
                        const msgRef = doc(db, FIRESTORE_PATHS.CHATS, threadId, FIRESTORE_PATHS.MESSAGES, msg.id);
                        batch.update(msgRef, { status: 'seen' });
                    });
                    await batch.commit();
                } catch (err) {
                    logger.error('Chat', 'Error marking messages as seen:', err);
                }
            }
        }, (err) => {
            logger.error('Chat', 'Messages listener error:', err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [friend, userId]);

    const handleSend = async () => {
        if (!newMessage.trim()) return;
        const threadId = [userId, friend.uid].sort().join('_');

        // Determine initial status based on friend's online status
        const initialStatus = friendOnline ? 'delivered' : 'sent';

        try {
            await addDoc(collection(db, FIRESTORE_PATHS.CHATS, threadId, FIRESTORE_PATHS.MESSAGES), {
                text: newMessage,
                senderId: userId,
                createdAt: serverTimestamp(),
                status: initialStatus
            });
            setNewMessage('');
            setShowEmojiPicker(false);
            // No manual refresh needed — onSnapshot handles it
        } catch (err) {
            logger.error('Chat', 'Error sending message:', err);
        }
    };

    const onEmojiClick = (emojiObject) => {
        setNewMessage(prev => prev + emojiObject.emoji);
    };

    const renderTicks = (status) => {
        if (status === 'seen') {
            return (
                <div className="flex -space-x-1.5 text-sky-400">
                    <Check size={14} strokeWidth={3} />
                    <Check size={14} strokeWidth={3} />
                </div>
            );
        }
        if (status === 'delivered') {
            return (
                <div className="flex -space-x-1.5 text-purple-200/50">
                    <Check size={14} strokeWidth={3} />
                    <Check size={14} strokeWidth={3} />
                </div>
            );
        }
        return <Check size={14} strokeWidth={3} className="text-purple-200/50" />;
    };

    return (
        <div className="flex-1 flex flex-col bg-gray-900 overflow-hidden relative">
            {/* Chat Header */}
            <div className="p-4 bg-gray-800 border-b border-gray-700 flex items-center gap-3 z-10">
                <button onClick={onBack} className="text-gray-400 hover:text-white transition-colors">
                    <ChevronLeft size={24} />
                </button>
                <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-bold text-white shadow-lg">
                        {friend.displayName?.charAt(0) || '?'}
                    </div>
                    <div className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-gray-800 rounded-full ${friendOnline ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                </div>
                <div className="flex-1">
                    <h3 className="text-white font-bold">{friend.displayName}</h3>
                    <span className={`text-xs ${friendOnline ? 'text-green-400' : 'text-gray-500'}`}>
                        {friendOnline ? 'Online • Active Now' : 'Offline'}
                    </span>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
                {loading && <p className="text-center text-gray-500 text-sm py-10">Loading conversation...</p>}
                {!loading && messages.length === 0 && (
                    <div className="text-center text-gray-500 mt-10">
                        <div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center mx-auto mb-4 border border-gray-700/50">
                            <Send size={24} className="opacity-20" />
                        </div>
                        <p className="text-sm">No messages yet. Say hi to {friend.displayName}!</p>
                    </div>
                )}
                {messages.map((msg, index) => {
                    const isMe = msg.senderId === userId;
                    const prevMsg = messages[index - 1];
                    const showAvatar = !isMe && (!prevMsg || prevMsg.senderId !== msg.senderId);

                    return (
                        <div key={msg.id} className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                            {!isMe && (
                                <div className="w-8 h-8 rounded-full flex-shrink-0">
                                    {showAvatar ? (
                                        <div className="w-full h-full rounded-full bg-gray-700 flex items-center justify-center text-[10px] font-bold text-white shadow-sm">
                                            {friend.displayName?.charAt(0) || '?'}
                                        </div>
                                    ) : (
                                        <div className="w-8" />
                                    )}
                                </div>
                            )}
                            <div className="flex flex-col gap-0.5 max-w-[75%]">
                                <div className={`px-4 py-2 rounded-2xl relative shadow-md transition-all ${
                                    isMe 
                                    ? 'bg-purple-600 text-white rounded-br-none hover:bg-purple-500 shadow-purple-500/20' 
                                    : 'bg-gray-800 text-white rounded-bl-none hover:bg-gray-750'
                                }`}>
                                    <p className="text-sm leading-relaxed break-words">{msg.text}</p>
                                    <div className={`flex items-center gap-1 mt-1 justify-end ${isMe ? 'opacity-90' : 'opacity-60'}`}>
                                        <span className={`text-[9px] font-medium uppercase tracking-tighter ${isMe ? 'text-purple-200' : 'text-gray-400'}`}>
                                            {msg.createdAt?.toDate ? new Date(msg.createdAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                        </span>
                                        {isMe && renderTicks(msg.status)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Emoji Picker Overlay */}
            {showEmojiPicker && (
                <div className="absolute bottom-20 left-4 right-4 z-50 shadow-2xl rounded-2xl overflow-hidden animate-in slide-in-from-bottom-5 duration-200">
                    <div className="relative">
                        <button 
                            onClick={() => setShowEmojiPicker(false)}
                            className="absolute -top-10 right-0 bg-gray-800 text-white p-2 rounded-full border border-gray-700 shadow-xl z-50"
                        >
                            <ChevronLeft size={20} className="rotate-90" />
                        </button>
                        <EmojiPicker 
                            onEmojiClick={onEmojiClick}
                            width="100%"
                            theme="dark"
                            skinTonesDisabled
                            searchDisabled
                        />
                    </div>
                </div>
            )}

            {/* Input */}
            <div className="p-4 bg-gray-800 border-t border-gray-700">
                <div className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-2xl px-3 py-1.5 focus-within:border-neon-cyan transition-all shadow-inner">
                    <button 
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className={`p-2 transition-colors rounded-full ${showEmojiPicker ? 'bg-neon-cyan/10 text-neon-cyan' : 'text-gray-500 hover:text-white hover:bg-gray-800'}`}
                    >
                        <Smile size={22} />
                    </button>
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => {
                            setNewMessage(e.target.value);
                            if (showEmojiPicker) setShowEmojiPicker(false);
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        placeholder="Type a message..."
                        className="flex-1 bg-transparent py-2.5 text-white text-sm focus:outline-none placeholder-gray-600"
                    />
                    <button 
                        onClick={handleSend} 
                        disabled={!newMessage.trim()}
                        className={`p-2 rounded-xl transition-all duration-300 ${
                            newMessage.trim() 
                            ? 'bg-neon-cyan text-black scale-100 shadow-neon active:scale-95' 
                            : 'bg-gray-800 text-gray-600 scale-90 cursor-not-allowed opacity-50'
                        }`}
                    >
                        <Send size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatView;
